import React, { useState, useEffect, useRef } from "react";
import { Volume2, Pause, Play, Loader2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

/**
 * AudioPlayer
 *
 * Plays the ElevenLabs-generated TTS for a patient narrative.
 *
 * Props:
 * - source: { type: 'owner' | 'shared', id: string }
 * - language: ISO code (en, es, hi, ...)
 */
export default function AudioPlayer({ source, language = "en" }) {
  const [enabled, setEnabled] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);
  const loadedLangRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/config");
        setEnabled(!!data.voice_enabled);
      } catch {
        setEnabled(false);
      }
    })();
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  // Reset when language changes
  useEffect(() => {
    if (loadedLangRef.current && loadedLangRef.current !== language) {
      audioRef.current?.pause();
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
      if (audioRef.current) audioRef.current.removeAttribute("src");
      loadedLangRef.current = null;
      setPlaying(false);
      setProgress(0);
      setDuration(0);
    }
  }, [language]);

  const ensureAudioLoaded = async () => {
    if (audioRef.current?.src && loadedLangRef.current === language) return audioRef.current;
    setLoading(true);
    try {
      const url = source.type === "shared"
        ? `${API}/shared/${source.id}/audio/${language}`
        : `${API}/reports/${source.id}/audio/${language}`;
      let blob;
      if (source.type === "shared") {
        const res = await fetch(url);
        if (!res.ok) throw new Error("audio fetch failed");
        blob = await res.blob();
      } else {
        const res = await api.get(`/reports/${source.id}/audio/${language}`, { responseType: "blob" });
        blob = res.data;
      }
      const blobUrl = URL.createObjectURL(blob);
      blobUrlRef.current = blobUrl;
      audioRef.current.src = blobUrl;
      await new Promise((resolve, reject) => {
        audioRef.current.onloadedmetadata = resolve;
        audioRef.current.onerror = reject;
      });
      setDuration(audioRef.current.duration || 0);
      loadedLangRef.current = language;
      return audioRef.current;
    } catch {
      toast.error("Could not load audio");
      throw new Error("audio");
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = async () => {
    try {
      const audio = await ensureAudioLoaded();
      if (audio.paused) { await audio.play(); setPlaying(true); }
      else { audio.pause(); setPlaying(false); }
    } catch (err) {
      // play() rejects when the user hasn't interacted yet, when audio fails to
      // load, or when the tab is throttled. ensureAudioLoaded already surfaces
      // load errors via a toast, so we only log here.
      if (process.env.NODE_ENV === "development") console.warn("Audio play/pause failed:", err);
    }
  };

  const onTimeUpdate = () => {
    const a = audioRef.current;
    if (a && a.duration) setProgress((a.currentTime / a.duration) * 100);
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a?.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * a.duration;
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60), ss = Math.floor(s % 60);
    return `${m}:${ss.toString().padStart(2, "0")}`;
  };

  if (enabled === null || enabled === false) return null;

  return (
    <div
      data-testid="audio-player"
      className="no-print flex items-center gap-3 p-3 rounded-lg border mb-6"
      style={{ borderColor: "var(--color-border)", background: "var(--bg-canvas)" }}
    >
      <button
        data-testid="audio-play-button"
        onClick={togglePlay}
        disabled={loading}
        className="w-10 h-10 rounded-full flex items-center justify-center text-white disabled:opacity-60 flex-shrink-0"
        style={{ background: "var(--color-primary)" }}
        aria-label={playing ? "Pause" : "Play"}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs" style={{ color: "var(--color-text-secondary)" }}>
          <Volume2 className="w-3.5 h-3.5" />
          <span className="font-medium" style={{ color: "var(--color-text-primary)" }}>{t(language, "listen")}</span>
          <span className="ml-auto tabular-nums">{fmt((audioRef.current?.currentTime || 0))} / {fmt(duration)}</span>
        </div>
        <div
          data-testid="audio-progress-bar"
          onClick={seek}
          className="mt-2 h-1.5 rounded-full cursor-pointer overflow-hidden"
          style={{ background: "var(--color-border)" }}
        >
          <div className="h-full transition-all" style={{ width: `${progress}%`, background: "var(--color-primary)" }} />
        </div>
      </div>

      <audio
        ref={audioRef}
        onTimeUpdate={onTimeUpdate}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        preload="none"
      />
    </div>
  );
}
